import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ModalController, NavController, PopoverController } from '@ionic/angular';
import { ColumnMode, SelectionType } from '@swimlane/ngx-datatable';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionDropdownComponent } from 'src/app/components/action-dropdown/action-dropdown.component';
import { ComposePage } from 'src/app/components/compose/compose.page';
import { EditTagLinksComponent } from 'src/app/components/edit-tag-links/edit-tag-links.component';
import { SelectedFilterMap } from 'src/app/components/filter-button/filter-button.component';
import { INTEGRATIONS, PROJECT_MEMBER_ROLES, routes, SUPPORT_STEP_ASSIGNEE_TYPES } from 'src/app/constants';
import {
  createCustomerRecipient,
  downloadCSV,
  getWhereClausesFromFilters,
  removeUndefinedFields,
} from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { Customer, WhereClause } from 'src/models';
import { PROJECT_VISIBILITY } from 'src/models/projects';
import { AnalyticsService } from 'src/services/analytics.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { GlobalCreateService } from 'src/services/global-create.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';
import { UserGroupsService } from 'src/services/user-groups.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-customer-list',
  templateUrl: './customer-list.page.html',
  styleUrls: ['./customer-list.page.scss'],
})
export class CustomerListPage implements OnInit {
  filters: SelectedFilterMap = new Map();

  allCustomers: Partial<Customer>[];
  selectedCustomers: Partial<Customer>[] = [];

  public readonly customers$: BehaviorSubject<Partial<Customer>[]> = new BehaviorSubject([]);
  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  routerSub: Subscription;
  connectedCrm: string;
  showArchived = false;
  limit = 60;
  cursor: any;
  sortField = 'name';
  sortDesc = false;
  selectAllPagesForBulkUpdate = false;
  showCreateButton = false;

  PROJECT_VISIBILITY = PROJECT_VISIBILITY;

  isLoadingAssignees = false;
  assigneeLookups: any = {};

  filterOptions = [
    {
      label: 'Customer Tag',
      id: 'tags.id',
      icon: 'pricetag',
      type: 'select',
      values: async () => {
        this.isLoading = true;

        const tags = await this.tagsService.list({
          limit: null,
          order: ['name'],
          descending: false,
        });

        const result = tags.data.map((tag) => {
          return {
            label: tag.name,
            id: tag.id,
          };
        });

        this.isLoading = false;

        return result;
      },
    },
    {
      label: 'Project',
      id: 'projectId',
      icon: 'folder-open',
      type: 'select',
    },
  ];

  select = [
    'id',
    'name',
    'primarySupportAssigneeId',
    'primarySupportAssigneeType',
    'secondarySupportAssigneeId',
    'secondarySupportAssigneeType',
    'replyTimeoutMinutes',
    'defaultTriageChannelId',
    'disableAutomatedTicketing',
    'botHandling',
    'autoresponder',
    'supportSteps',
    'slackTeamIds',
    'slackTeamNames',
    'assignToTaggedUserEnabled',
    'createdAt',
    'updatedAt',
    'createdBy',
    'updatedBy',
    'slackChannel.id',
    'slackChannel.name',
    'slackChannel.isPrivate',
    'isInternal',
    'externalCrmMetadata',
    'customerViewEnabled',
    'adminEmails',
    'slackEnabled',
    'emailDomains',
    'emailDomainsWrapper.emailDomains',
    'supportStepRuleset',
    'tags.name',
    'tags.id',
    'project.id',
    'project.name',
    'project.visibility',
    'type',
    'inboundEmailRule.email',
  ];

  constructor(
    private customersService: CustomersService,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navController: NavController,
    private tagsService: TagsService,
    private analyticsService: AnalyticsService,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private location: Location,
    private usersSrvc: UsersService,
    private userGroupsService: UserGroupsService,
    private globalCreateService: GlobalCreateService,
    private searchablePopoverService: SearchablePopoverService,
    private msgSrvc: MessageService,
    private router: Router,
  ) {}

  async ngOnInit() {
    if (this.authService.tenant.accountsEnabled) {
      // redirect to channels list page instead
      this.router.navigate(['/dashboard/channels']);
      return;
    }

    if (this.authService.tenant.integrations[INTEGRATIONS.salesforce.key]?.enabled) {
      this.connectedCrm = 'Salesforce';
    } else if (this.authService.tenant.integrations[INTEGRATIONS.hubspot.key]?.enabled) {
      this.connectedCrm = 'Hubspot';
    }

    // Allow creating channels if you're a tenant admin or an owner of any project
    this.showCreateButton =
      this.authService.isTenantAdmin || this.authService.projects.some((p) => p.role === PROJECT_MEMBER_ROLES.OWNER);

    this.loadCustomers();

    this.isLoadingAssignees = true;
    await Promise.all([this.loadSupportUsers(), this.loadUserGroups()]);
    this.isLoadingAssignees = false;
  }

  fetchCustomers = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<Customer>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      // search: this.searchText || undefined,
      select: this.select,
    };

    const where: WhereClause[] = [];

    const filterWheres = getWhereClausesFromFilters(this.filters);
    where.push(...filterWheres);

    if (!this.showArchived) {
      where.push({
        field: 'slackChannel.archivedAt',
        operator: '==',
        value: null,
      });
    }

    if (this.searchText) {
      where.push({
        field: 'name',
        operator: 'like',
        value: `%${this.searchText}%`,
      });
    }

    if (this.authService.selectedProject) {
      where.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    if (where.length) {
      options.where = where;
    }

    const result = await this.customersService.list(options);

    return result;
  };

  customerListDataSource = createDataSource(this.fetchCustomers);

  async loadCustomers() {
    this.isLoading = true;

    await this.customerListDataSource.firstPage();

    this.isLoading = false;
  }

  async reloadCustomers() {
    this.isLoading = true;

    await this.customerListDataSource.reloadPage();

    this.isLoading = false;
  }

  async loadSupportUsers() {
    const supportUsers = await this.usersSrvc.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'name',
          operator: '!=',
          value: null,
        },
        {
          field: 'slackId',
          operator: '!=',
          value: null,
        },
        {
          field: 'email',
          operator: '!=',
          value: null,
        },
      ],
    });
    supportUsers.data.forEach((u) => {
      this.assigneeLookups[u.id] = {
        id: u.id,
        displayName: `${u.name}${u.email ? ` (${u.email})` : ''}`,
        name: u.name,
        email: u.email,
        slackId: u.slackId,
        type: SUPPORT_STEP_ASSIGNEE_TYPES.USER,
      };
    });
  }

  async loadUserGroups() {
    const userGroups = await this.userGroupsService.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'name',
          operator: '!=',
          value: null,
        },
      ],
    });

    userGroups.data.forEach((u) => {
      this.assigneeLookups[u.id] = {
        id: u.id,
        displayName: `${u.name} (Group)`,
        name: u.name,
        type: SUPPORT_STEP_ASSIGNEE_TYPES.TEAM,
      };
    });
  }

  showAssigneeOptions(customer: Customer, isPrimary: boolean) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: isPrimary ? customer.primarySupportAssigneeId : customer.secondarySupportAssigneeId,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: true,
      callback: async (id: string, _name: string, object: any) => {
        const assigneeType = object?.type ?? SUPPORT_STEP_ASSIGNEE_TYPES.USER;

        const updates: Partial<Customer> = {};
        if (isPrimary) {
          customer.primarySupportAssigneeId = !id || id === customer.primarySupportAssigneeId ? null : id;
          updates.primarySupportAssigneeType = assigneeType;
          updates.primarySupportAssigneeId = customer.primarySupportAssigneeId;
        } else {
          customer.secondarySupportAssigneeId = !id || id === customer.secondarySupportAssigneeId ? null : id;
          updates.secondarySupportAssigneeType = assigneeType;
          updates.secondarySupportAssigneeId = customer.secondarySupportAssigneeId;
        }

        this.updateCustomer(customer.id, updates);
      },
    });
  }

  showSLAOptions(customer: Customer) {
    this.searchablePopoverService.slas({
      event,
      selectedSlaId: customer.supportStepRuleset?.id,
      callback: (id: string, _title: string, sla: any) => {
        this.updateCustomer(customer.id, {
          supportStepRulesetId: id,
        });
        customer.supportStepRuleset = sla;
      },
    });
  }

  onSelect({ selected }) {
    this.selectedCustomers.splice(0, this.selectedCustomers.length);
    this.selectedCustomers.push(...selected);
    this.selectAllPagesForBulkUpdate = false;
  }

  getRowClass() {
    return 'border-bottom';
  }

  async composeEmail() {
    this.analyticsService.trackEvent('Customer List', 'Bulk Compose Email');

    const emails = this.selectedCustomers.map((customer) => {
      return createCustomerRecipient(customer);
    });

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        emails,
        showTemplates: true,
      },
      cssClass: 'modal-large',
    });
    modal.present();

    this.selectedCustomers = [];
  }

  composeSlack() {
    this.analyticsService.trackEvent('Customer List', 'Bulk Compose Slack');

    const customerIds = this.selectAllPagesForBulkUpdate
      ? 'all'
      : this.selectedCustomers
          .map((customer) => {
            return customer.id;
          })
          .join(',');

    this.navController.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: removeUndefinedFields({
        customerIds,
        previousUrl: this.location.path(),
      }),
    });

    this.selectedCustomers = [];
  }

  openSlackChannel(slackChannelId: string) {
    window.open(`https://slack.com/app_redirect?channel=${slackChannelId}`);
  }

  openLink(url: string) {
    window.open(url, '_blank');
  }

  showNextPage() {
    this.pageCount += 1;
  }

  async manageTags() {
    const modal = await this.modalCtrl.create({
      component: EditTagLinksComponent,
      componentProps: {
        objectIds: this.selectedCustomers.map((c) => c.id),
        addCallback: async (tagId, customerIds) => {
          await this.tagsService.createCustomerLinks(tagId, customerIds);
          await this.reloadCustomers();
        },
        removeCallback: async (tagId, customerIds) => {
          await this.tagsService.deleteCustomerLinks(tagId, customerIds);
          await this.reloadCustomers();
        },
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.selectedCustomers = [];
    });
  }

  async showEditOptions() {
    const options = [
      {
        label: 'Edit tags',
        icon: 'pricetags-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number) => {
          popover.dismiss();

          switch (index) {
            case 0: {
              this.manageTags();

              break;
            }
            default:
              break;
          }
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  async addIndividualCustomer() {
    await this.globalCreateService.customer();
  }

  handleFilters() {
    this.loadCustomers();
  }

  async updateCustomer(customerId: string, updates: Partial<Customer>) {
    try {
      await this.customersService.update(customerId, updates);

      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      console.log(err);

      this.msgSrvc.show('Something went wrong');
    }
  }

  getTagsForRow(row: any) {
    return row.tags?.map((tag) => tag.name).join(', ');
  }

  async editTagForCustomer(customerId: string, tagId: string) {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options: [
          {
            label: 'Remove tag',
            icon: 'close-circle',
          },
        ],
        callback: async (index: number, _label: string) => {
          popover.dismiss();

          if (index === 0) {
            await this.tagsService.deleteCustomerLinks(tagId, [customerId]);
            this.loadCustomers();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  addTagToCustomer(customerId: string) {
    this.searchablePopoverService.tags({
      event,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Customer List', 'Add Tag From Row');

        await this.tagsService.createCustomerLinks(id, [customerId]);

        this.reloadCustomers();
      },
    });
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.loadCustomers();
  }

  async linkCrm(row: Customer) {
    this.searchablePopoverService.externalCrmAccounts({
      selectedAccountId: row.externalCrmMetadata?.id,
      initialSearchText: row.name,
      event,
      callback: async (id: string, name: string) => {
        const alert = await this.alertCtrl.create({
          header: 'Confirm account linking',
          subHeader: `This will link the ${name} profile to the ${row.name} customer`,
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Confirm',
              handler: async () => {
                await this.customersService.update(row.id, {
                  externalCrmMetadata: { id },
                });
                this.reloadCustomers();
              },
            },
          ],
        });

        await alert.present();
      },
    });
  }

  async unlinkCrm(row: Customer) {
    await this.customersService.update(row.id, {
      externalCrmMetadata: { id: null },
    });

    this.reloadCustomers();
  }

  async exportAsCsv() {
    this.analyticsService.trackEvent('Customer List', 'Export CSV');

    const date = new Date();

    // Get unpaginated list
    const customers = await this.fetchCustomers({
      limit: null,
    });

    const results = customers.data.map((c) => {
      return {
        ...c,
        primarySupportAssigneeName: c.primarySupportAssigneeId
          ? this.assigneeLookups[c.primarySupportAssigneeId]?.name
          : '',
        primarySupportAssigneeType: c.primarySupportAssigneeId ? c.primarySupportAssigneeType ?? 'user' : '',
        secondarySupportAssigneeName: c.secondarySupportAssigneeId
          ? this.assigneeLookups[c.secondarySupportAssigneeId]?.name
          : '',
        secondarySupportAssigneeType: c.secondarySupportAssigneeId ? c.secondarySupportAssigneeType ?? 'user' : '',
        slackTeamIds: c.slackTeams?.map((t) => t.id).join(','),
        slackTeamNames: c.slackTeams?.map((t) => t.name).join(','),
        slackChannelName: c.slackChannel?.name ?? '',
      };
    });

    const headers = [
      'name',
      'primarySupportAssigneeId',
      'primarySupportAssigneeName',
      'primarySupportAssigneeType',
      'secondarySupportAssigneeId',
      'secondarySupportAssigneeName',
      'secondarySupportAssigneeType',
      'replyTimeoutMinutes',
      'defaultTriageChannelId',
      'disableAutomatedTicketing',
      'botHandling',
      'autoresponder',
      'supportSteps',
      'slackTeamIds',
      'slackTeamNames',
      'assignToTaggedUserEnabled',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
      'slackChannelId',
      'slackChannelName',
      'isInternal',
      'externalCrmMetadata',
      'customerViewEnabled',
      'adminEmails',
      'slackEnabled',
      'emailDomains',
      'id',
      'supportStepRuleset',
      'tags',
    ];

    downloadCSV(results, headers, `customer-export-${date.getTime()}`);
  }
}
